import type { BoardSummary } from "@mn/shared";
import { useEffect, useState, useSyncExternalStore } from "react";
import { ApiError, api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useBoard } from "../BoardContext";
import { useBoardUI } from "../store";

const cache = new Map<string, BoardSummary>();

// First-use hint under tiles; cleared the first time any board is opened from a tile.
const HINT_KEY = "mn_hint_tile_done";
const hintListeners = new Set<() => void>();
const hintDone = () => {
  if (location.pathname.startsWith("/print/")) return true; // never in exports
  try {
    return localStorage.getItem(HINT_KEY) === "1";
  } catch {
    return true;
  }
};
export function markTileHintDone() {
  try {
    localStorage.setItem(HINT_KEY, "1");
  } catch {}
  for (const l of hintListeners) l();
}
const useTileHint = () =>
  useSyncExternalStore(
    (l) => {
      hintListeners.add(l);
      return () => hintListeners.delete(l);
    },
    () => !hintDone(),
  );

/** Tile that opens a nested board on double-click. Title/icon come from the SQL board. */
export function BoardTileBody({ childBoardId, alias }: { childBoardId: string; alias?: boolean }) {
  const { openBoard } = useBoard();
  const t = useT();
  const showHint = useTileHint();
  const [b, setB] = useState<BoardSummary | null>(cache.get(childBoardId) ?? null);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    let alive = true;
    api<{ board: BoardSummary }>(`/api/boards/${childBoardId}`)
      .then((r) => {
        cache.set(childBoardId, r.board);
        if (alive) {
          setB(r.board);
          setLocked(false);
        }
      })
      .catch((e) => {
        if (alive && e instanceof ApiError && e.code === "private") setLocked(true);
      });
    const onUpdated = (e: Event) => {
      const b = (e as CustomEvent<BoardSummary>).detail;
      if (b?.id === childBoardId) {
        cache.set(childBoardId, b);
        setB(b);
      }
    };
    window.addEventListener("mn:board-updated", onUpdated);
    return () => {
      alive = false;
      window.removeEventListener("mn:board-updated", onUpdated);
    };
  }, [childBoardId]);
  const hot = useBoardUI((s) => s.tileDrop === childBoardId);
  if (locked)
    return (
      <div
        data-tile={childBoardId}
        className="flex flex-col items-center gap-2 p-2 select-none opacity-70"
        title={t.visibility.lockedHint}
      >
        <div className="display flex size-[96px] items-center justify-center rounded-[22px] border border-dashed border-border text-4xl text-dim">
          🔒
        </div>
        <span className="line-clamp-2 w-full text-center text-[13px] leading-tight text-dim">
          {t.visibility.lockedTile}
        </span>
      </div>
    );
  const title = b?.title ?? "…";
  const letter = (title.trim()[0] ?? "?").toUpperCase();
  return (
    <div
      data-tile={childBoardId}
      className={`flex flex-col items-center gap-2 p-2 select-none ${hot ? "scale-110" : ""}`}
      style={{ transition: "transform 120ms" }}
      onDoubleClick={() => openBoard(childBoardId)}
      title={b?.privateOwnerId ? `${title} · ${t.visibility.privatePill}` : title}
    >
      <div
        className="display relative flex size-[96px] items-center justify-center rounded-[22px] border border-border text-5xl text-text shadow-panel"
        style={{ background: b?.iconColor ?? "#2a2a2a" }}
      >
        {b?.icon ?? letter}
        {b?.privateOwnerId ? (
          <span className="absolute -left-1 -top-1 rounded-full border border-border bg-panel px-1 text-[11px] leading-4">
            🔒
          </span>
        ) : null}
        {alias ? (
          <span className="absolute -right-1 -top-1 rounded-full border border-border bg-panel px-1 text-[11px] leading-4 text-muted">
            ↗
          </span>
        ) : null}
      </div>
      <span className="line-clamp-2 w-full text-center text-[13px] leading-tight">{title}</span>
      {b && b.cardCount > 0 ? <span className="text-[11px] text-dim">{b.cardCount}</span> : null}
      {showHint ? <span className="text-[10px] italic text-dim">{t.tileHint}</span> : null}
    </div>
  );
}
